import { DateTime, Info } from "luxon";
import { useAppStore } from "../../../stores/RootStore";
import { DateShift, Equipment, FShift } from "../../../types/types";
import { Button } from "../../../ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/Popover";
import { EquipmentReportTableData, DataRow } from "../../../stores/TabsStore";
import _ from "lodash";
import { t } from "i18next";

export interface ExcludeFilters {
  originalDays: string[],
  originalWeekdays: number[],
  originalShifts: DateShift[],
  originalEquipments: string[],
  filteredDays: string[],
  filteredWeekdays: number[],
  filteredShifts: DateShift[],
  filteredEquipments: string[],
  diffDays: string[],
  diffWeekdays: number[],
  diffShifts: DateShift[],
  diffEquipments: string[]
}

const ExcludeData = ({excludeFilters}:{excludeFilters: ExcludeFilters}) => {
  const {equipments} = useAppStore()
  return (
    excludeFilters?.diffDays.length! > 0 || excludeFilters?.diffWeekdays.length! > 0 || excludeFilters?.diffShifts.length! > 0 || excludeFilters?.diffEquipments.length! > 0 ?
    <Popover>
      <PopoverTrigger asChild>
        <Button size="xs" variant="outline">Исключения</Button>
      </PopoverTrigger>
      <PopoverContent align="start">
        <div className="flex flex-wrap gap-2">
          {excludeFilters?.diffDays.length! > 0 ? <div className="w-full text-sm font-medium">Дни</div> : <></>}
          {excludeFilters?.diffDays.length! > 0 ? excludeFilters?.diffDays?.map((d, i) => (<div key={i} className="bg-muted d rounded-sm px-2 text-sm">{DateTime.fromSQL(d!).toISODate()}</div>)) : <></>}
          {excludeFilters?.diffWeekdays.length! > 0 ? <div className="w-full text-sm font-medium">Дни недели</div> : <></>}
          {excludeFilters?.diffWeekdays.length! > 0 ? excludeFilters?.diffWeekdays.map((d, i) => (<div key={i} className="bg-muted w rounded-sm px-2 text-sm capitalize">{Info.weekdays('long')[d - 1]}</div>)) : <></>}
          {excludeFilters?.diffShifts.length! > 0 ? <div className="w-full text-sm font-medium">Смены</div> : <></>}
          {excludeFilters?.diffShifts.length! > 0 ? excludeFilters?.diffShifts.map((d, i) => (<div key={i} className="bg-muted s rounded-sm px-2 text-sm">{d.label}</div>)) : <></>}
          {excludeFilters?.diffEquipments.length! > 0 ? <div className="w-full text-sm font-medium">Оборудование</div> : <></>}
          {excludeFilters?.diffEquipments.length! > 0 ? excludeFilters?.diffEquipments.map((d, i) => (<div key={i} className="bg-muted e rounded-sm px-2 text-sm">{equipments?.find(e => e.id === d!)?.attributes.name}</div>)) : <></>}
        </div>
      </PopoverContent>
    </Popover>
    :
    <></>
  )
}

const ExcludeDataText = ({excludeFilters}:{excludeFilters: ExcludeFilters}) => {
  const {equipments} = useAppStore()
  let days = excludeFilters?.diffDays.map(x => DateTime.fromSQL(x).toFormat("dd.MM.yyyy")) || []
  let weekdays = excludeFilters?.diffWeekdays.map(x => Info.weekdays()[x - 1])
  let shifts = excludeFilters?.diffShifts.map(s => s.label)
  let equipment = equipments?.filter(x => excludeFilters?.diffEquipments.includes(String(x.id))).map(x => x.attributes.name)
  let stringData = days.concat(weekdays, shifts, equipment ? equipment : []).join(", ")

  return (
    excludeFilters?.diffDays.length! > 0 || excludeFilters?.diffWeekdays.length! > 0 || excludeFilters?.diffShifts.length! > 0 || excludeFilters?.diffEquipments.length! > 0 ?
    <div className="text-xs space-x-2">
      <span>{t('excludes')}:</span>
      <span>{stringData}</span>
    </div>
    :
    <></>
  )
}

const getExcludeDataText = (excludeFilters: ExcludeFilters, equipments: Equipment[]) => {
  let days = excludeFilters?.diffDays.map(x => DateTime.fromSQL(x).toFormat("dd.MM.yyyy")) || []
  let weekdays = excludeFilters?.diffWeekdays.map(x => Info.weekdays()[x])
  let shifts = excludeFilters?.diffShifts.map(s => s.label)
  let equipment = equipments?.filter(x => excludeFilters?.diffEquipments.includes(String(x.id))).map(x => x.attributes.name)
  let stringData = days.concat(weekdays, shifts, equipment ? equipment : []).join(", ")

  return excludeFilters?.diffDays.length! > 0 || excludeFilters?.diffWeekdays.length! > 0 || excludeFilters?.diffShifts.length! > 0 || excludeFilters?.diffEquipments.length! > 0 ? `${t('excludes')}: ${stringData}` : ""
}

export default ExcludeData;
export {
  ExcludeDataText,
  getExcludeDataText
}

export function getExcludeFilters(originalData: DataRow[], filteredData: DataRow[]):ExcludeFilters {
  const originalDays = originalData.map((d) => d.dateShift.start_day.toSQLDate()!).filter((value, index, array) => array.indexOf(value) === index);
  const originalWeekdays = originalData.map((d) => d.dateShift.start_day.weekday).filter((value, index, array) => array.indexOf(value) === index);
  const originalShifts = _.uniqBy(originalData.map((d) => d.dateShift).filter((value, index, array) => array.indexOf(value) === index), "id")
  const originalEquipments = originalData.map((d) => d.equipment.id).filter((value, index, array) => array.indexOf(value) === index);

  const filteredDays = _.orderBy(filteredData.map((d) => d.dateShift.start_day.toSQLDate()!).filter((value, index, array) => array.indexOf(value) === index), "dateShift.start_day");
  const filteredWeekdays = filteredData.map((d) => d.dateShift.start_day.weekday).filter((value, index, array) => array.indexOf(value) === index);
  const filteredShifts = _.orderBy(filteredData.map((d) => d.dateShift).filter((value, index, array) => array.indexOf(value) === index), "start_day");
  const filteredEquipments = filteredData.map((d) => d.equipment.id).filter((value, index, array) => array.indexOf(value) === index);

  const diffDays = originalDays.filter(x => !filteredDays.includes(x));
  const diffWeekdays = originalWeekdays.filter(x => !filteredWeekdays.includes(x));
  const diffShifts = originalShifts.filter(x => !filteredShifts.map(fs => fs.id).includes(x.id));
  const diffEquipments = originalEquipments.filter(x => !filteredEquipments.includes(x));

  // console.log(filteredDays)

  return {
    originalDays: originalDays,
    originalWeekdays: originalWeekdays,
    originalShifts: originalShifts,
    originalEquipments: originalEquipments,
    filteredDays: filteredDays,
    filteredWeekdays: filteredWeekdays,
    filteredShifts: filteredShifts,
    filteredEquipments: filteredEquipments,
    diffDays: diffDays,
    diffWeekdays: diffWeekdays,
    diffShifts: diffShifts,
    diffEquipments: diffEquipments
  }
}
